import { hasEventEnded, isEventUnderway } from '@sixam/shared/event-time';

import type { EventAttendanceStatus } from '@/lib/event-attendance';
import type { Event } from '@/lib/events';

export type AdminEventListFilter = 'upcoming' | 'drafts' | 'past';

export type AdminEventListCounts = Readonly<{
  drafts: number;
  past: number;
  upcoming: number;
}>;

export type MemberEventHeadlineBadge = 'Active' | 'Going' | 'Pending' | null;

export function adminEventListCounts(events: readonly Event[], now: Date): AdminEventListCounts {
  return {
    drafts: events.filter((event) => event.status === 'draft').length,
    past: events.filter((event) => event.status === 'published' && hasEventEnded(event, now))
      .length,
    upcoming: events.filter((event) => event.status !== 'draft' && !hasEventEnded(event, now))
      .length,
  };
}

export function filterAdminEvents(
  events: readonly Event[],
  filter: AdminEventListFilter,
  now: Date
): Event[] {
  if (filter === 'drafts') {
    return events.filter((event) => event.status === 'draft');
  }

  if (filter === 'past') {
    return events.filter((event) => event.status === 'published' && hasEventEnded(event, now));
  }

  return events.filter((event) => event.status !== 'draft' && !hasEventEnded(event, now));
}

export function memberEventHeadlineBadge(
  event: Event,
  attendanceStatus: EventAttendanceStatus,
  now: Date
): MemberEventHeadlineBadge {
  if (attendanceStatus === 'cancelled') {
    return null;
  }
  if (event.status === 'published' && isEventUnderway(event, now)) {
    return 'Active';
  }
  if (attendanceStatus === 'pending') {
    return 'Pending';
  }
  if (attendanceStatus === 'registered') {
    return 'Going';
  }
  return null;
}

export function searchAdminEvents(events: readonly Event[], query: string): Event[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return [...events];
  }

  return events.filter((event) =>
    [event.title, event.description, event.place_name, event.place_address, event.status].some(
      (value) => value?.toLowerCase().includes(normalizedQuery)
    )
  );
}
